import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, KeyRound, Landmark, Pencil, Plus, Trash2, Vault } from "lucide-react";
import * as api from "../../api/utentiCassaforte";
import { DatiUtenza, ETICHETTE_TIPO, ORDINE_TIPI, OrigineDato, TipoUtenteCassaforte, UtentiContiSede } from "../../api/utentiCassaforte";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Badge, BadgeColor } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { ExcelUtenti } from "./ExcelUtenti";
import { etichettaMatricola } from "../../utils/matricola";

const ORIGINE: Record<OrigineDato, { etichetta: string; colore: BadgeColor; titolo: string }> = {
  aznext: { etichetta: "AZNext", colore: "primary", titolo: "Importato da AZNext" },
  excel: { etichetta: "Excel", colore: "accent", titolo: "Caricato dall'Excel della sede" },
  manuale: { etichetta: "A mano", colore: "neutral", titolo: "Scritto a mano: l'import AZNext non lo sovrascrive" },
};

function BadgeOrigine({ origine }: { origine: OrigineDato | null }) {
  if (!origine) return null;
  const o = ORIGINE[origine];
  return (
    <span title={o.titolo}>
      <Badge color={o.colore}>{o.etichetta}</Badge>
    </span>
  );
}

function dataOra(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
}

const SELECT = "h-10 w-full rounded-lg border border-line bg-white px-3 text-sm text-ink disabled:cursor-not-allowed disabled:bg-black/5";

/**
 * Utenti autorizzati e conti — chi può aprire le casseforti di questa sede e
 * dove vanno i soldi: utenze AZNext (Super User, User, Service, Cit, Super User
 * tecnico), conto tecnico per cassaforte, conto di accredito, e l'avviso
 * «una sede, un conto» calcolato dal server.
 *
 * Le utenze non sono utenti dell'app e le loro password non si registrano.
 * Ogni modifica fatta qui vale come «a mano» e l'import AZNext non la
 * sovrascrive. Chi non può modificare la sede vede IBAN e conti già mascherati
 * dal server.
 */
export function UtentiConti({ sedeId }: { sedeId: string }) {
  const { showToast } = useToast();
  const [dati, setDati] = useState<UtentiContiSede | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [versione, setVersione] = useState(0);

  useEffect(() => {
    let annullato = false;
    setIsLoading(true);
    api
      .getUtentiContiSede(sedeId)
      .then((d) => {
        if (!annullato) setDati(d);
      })
      .catch((err) => {
        if (!annullato) showToast(getErrorMessage(err, "Impossibile caricare utenti e conti della sede."), "error");
      })
      .finally(() => {
        if (!annullato) setIsLoading(false);
      });
    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sedeId, versione]);

  // Dopo ogni scrittura si rilegge tutto: l'avviso dipende anche dalle altre sedi.
  const ricarica = () => setVersione((v) => v + 1);

  return (
    <section className="mb-8 rounded-card border border-line bg-white p-5 shadow-card">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-ink/50">
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          Utenti autorizzati e conti
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          {dati && <span className="text-xs text-ink/45">{dati.utenti.length} utenze</span>}
          {dati && <ExcelUtenti ambito="sede" id={sedeId} puoCaricare={dati.can_edit} onApplicato={ricarica} />}
        </div>
      </div>

      {isLoading && !dati ? (
        <p className="py-4 text-center text-sm text-ink/50">Caricamento…</p>
      ) : !dati ? (
        <p className="py-4 text-center text-sm text-ink/50">Dati non disponibili.</p>
      ) : (
        <>
          <Avvisi avvisi={dati.avvisi} />
          <Utenze sedeId={sedeId} dati={dati} onCambiato={ricarica} />
          <ContiTecnici sedeId={sedeId} dati={dati} onCambiato={ricarica} />
          <ContoAccredito sedeId={sedeId} dati={dati} onCambiato={ricarica} />
        </>
      )}
    </section>
  );
}

function Avvisi({ avvisi }: { avvisi: UtentiContiSede["avvisi"] }) {
  if (avvisi.conti_condivisi.length === 0 && avvisi.piu_conti.length === 0) return null;
  return (
    <div role="status" className="mb-4 space-y-2 rounded-lg border border-[#f97316]/30 bg-[#f97316]/5 px-4 py-3 text-sm text-ink">
      {avvisi.conti_condivisi.map((c) => (
        <p key={c.conto} className="flex gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#c2410c]" aria-hidden="true" />
          <span>
            Conto tecnico <span className="font-mono font-semibold">{c.conto}</span> condiviso con altre sedi:{" "}
            {c.altre_sedi.map((s, i) => (
              <span key={s.id}>
                {i > 0 && ", "}
                <Link to={`/sedi/${s.id}`} className="font-semibold text-primary hover:underline">
                  {s.nome_sede ?? "Sede senza nome"}
                </Link>
                {s.cliente_nome && <span className="text-ink/50"> ({s.cliente_nome})</span>}
              </span>
            ))}
            .
          </span>
        </p>
      ))}
      {avvisi.piu_conti.length > 0 && (
        <p className="flex gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#c2410c]" aria-hidden="true" />
          <span>
            Sede con più conti tecnici:{" "}
            {avvisi.piu_conti.map((c, i) => (
              <span key={c}>
                {i > 0 && ", "}
                <span className="font-mono font-semibold">{c}</span>
              </span>
            ))}
            . La regola è una sede, un conto.
          </span>
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utenze
// ---------------------------------------------------------------------------

function Utenze({ sedeId, dati, onCambiato }: { sedeId: string; dati: UtentiContiSede; onCambiato: () => void }) {
  const { showToast } = useToast();
  const [inModifica, setInModifica] = useState<string | null>(null);
  const [eliminandoId, setEliminandoId] = useState<string | null>(null);
  const cassefortiSede = dati.casseforti.map((c) => ({ id: c.device_id, matricola: c.matricola }));

  async function aggiungi(d: DatiUtenza) {
    await api.createUtenza(sedeId, d);
    showToast("Utenza aggiunta.", "success");
    onCambiato();
  }

  async function modifica(id: string, d: DatiUtenza) {
    await api.updateUtenza(id, d);
    setInModifica(null);
    showToast("Utenza aggiornata.", "success");
    onCambiato();
  }

  async function rimuovi(u: api.UtenteCassaforte) {
    if (!window.confirm(`Togliere l'utenza ${ETICHETTE_TIPO[u.tipo]} «${u.username}» da questa sede?`)) return;
    setEliminandoId(u.id);
    try {
      await api.deleteUtenza(u.id);
      showToast("Utenza rimossa.", "success");
      onCambiato();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile rimuovere l'utenza."), "error");
    } finally {
      setEliminandoId(null);
    }
  }

  const gruppi = ORDINE_TIPI.map((tipo) => ({ tipo, righe: dati.utenti.filter((u) => u.tipo === tipo) })).filter(
    (g) => g.righe.length > 0
  );

  return (
    <div className="mb-6">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-ink/45">Utenze delle casseforti</h3>
      {gruppi.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-4 py-4 text-center text-sm text-ink/45">
          Nessuna utenza registrata per le casseforti di questa sede.
        </p>
      ) : (
        gruppi.map((g) => (
          <div key={g.tipo} className="mb-3">
            <p className="mb-1 text-xs font-semibold text-ink/60">
              {ETICHETTE_TIPO[g.tipo]} <span className="font-normal text-ink/40">({g.righe.length})</span>
            </p>
            <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
              {g.righe.map((u) =>
                inModifica === u.id ? (
                  <li key={u.id} className="px-3 py-2.5">
                    <FormUtenza
                      iniziale={{ tipo: u.tipo, username: u.username, nominativo: u.nominativo, device_id: u.device?.id ?? null }}
                      casseforti={
                        u.device && !cassefortiSede.some((c) => c.id === u.device!.id) ? [...cassefortiSede, u.device] : cassefortiSede
                      }
                      cassaforteBloccata={u.visto_in_aznext_il !== null}
                      etichettaSalva="Salva"
                      onSalva={(d) => modifica(u.id, d)}
                      onAnnulla={() => setInModifica(null)}
                    />
                  </li>
                ) : (
                  <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                    <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_auto_auto]">
                      <p className="truncate font-mono text-sm font-semibold text-ink">{u.username}</p>
                      <p className="truncate text-sm text-ink/70">{u.nominativo ?? "—"}</p>
                      <p className="text-xs text-ink/50">{u.device ? `Cassaforte ${etichettaMatricola(u.device.matricola)}` : "Cassaforte non indicata"}</p>
                      <p className="text-xs text-ink/50" title="Il conto su cui AZNext accredita questa utenza">
                        {u.conto_tecnico_aznext ? <>Conto AZNext <span className="font-mono">{u.conto_tecnico_aznext}</span></> : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <BadgeOrigine origine={u.origine} />
                      {dati.can_edit && (
                        <>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setInModifica(u.id)} aria-label={`Modifica ${u.username}`}>
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            loading={eliminandoId === u.id}
                            onClick={() => rimuovi(u)}
                            aria-label={`Rimuovi ${u.username}`}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                )
              )}
            </ul>
          </div>
        ))
      )}

      {dati.can_edit && (
        <div className="mt-3 rounded-lg border border-dashed border-line p-3">
          <FormUtenza casseforti={cassefortiSede} etichettaSalva="Aggiungi" onSalva={aggiungi} azzeraDopo />
          <p className="mt-2 text-xs text-ink/45">Le password delle utenze non si registrano in Wink on Support.</p>
        </div>
      )}
    </div>
  );
}

function FormUtenza({
  iniziale,
  casseforti,
  etichettaSalva,
  onSalva,
  onAnnulla,
  azzeraDopo = false,
  cassaforteBloccata = false,
}: {
  iniziale?: DatiUtenza;
  casseforti: { id: string; matricola: string | null }[];
  /** Utenza vista da AZNext: la cassaforte la aggiorna l'import, stessa regola della rotta. */
  cassaforteBloccata?: boolean;
  etichettaSalva: string;
  onSalva: (d: DatiUtenza) => Promise<void>;
  onAnnulla?: () => void;
  azzeraDopo?: boolean;
}) {
  const { showToast } = useToast();
  const [tipo, setTipo] = useState<TipoUtenteCassaforte>(iniziale?.tipo ?? "user");
  const [username, setUsername] = useState(iniziale?.username ?? "");
  const [nominativo, setNominativo] = useState(iniziale?.nominativo ?? "");
  // La cassaforte unica si propone solo per un'utenza nuova: in modifica resta
  // quella che l'utenza ha, anche se nessuna.
  const [deviceId, setDeviceId] = useState(
    iniziale ? iniziale.device_id ?? "" : casseforti.length === 1 ? casseforti[0].id : ""
  );
  const [isSaving, setIsSaving] = useState(false);

  async function salva() {
    setIsSaving(true);
    try {
      await onSalva({ tipo, username: username.trim(), nominativo: nominativo.trim() || null, device_id: deviceId || null });
      if (azzeraDopo) {
        setUsername("");
        setNominativo("");
      }
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare l'utenza."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[150px] flex-1">
        <label className="flex flex-col gap-1.5 text-sm font-medium text-ink">
          Tipo utente
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoUtenteCassaforte)} className={SELECT}>
            {ORDINE_TIPI.map((t) => (
              <option key={t} value={t}>
                {ETICHETTE_TIPO[t]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="min-w-[140px] flex-1">
        <Input label="USER" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
      </div>
      <div className="min-w-[180px] flex-[2]">
        <Input label="Nominativo" value={nominativo} onChange={(e) => setNominativo(e.target.value)} />
      </div>
      <div className="min-w-[150px] flex-1">
        <label className="flex flex-col gap-1.5 text-sm font-medium text-ink">
          Cassaforte
          <select
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            className={SELECT}
            disabled={cassaforteBloccata}
            title={cassaforteBloccata ? "La cassaforte di un'utenza vista da AZNext la aggiorna l'import" : undefined}
          >
            <option value="">—</option>
            {casseforti.map((c) => (
              <option key={c.id} value={c.id}>
                {etichettaMatricola(c.matricola)}
              </option>
            ))}
          </select>
        </label>
        {cassaforteBloccata && <p className="mt-1 text-xs text-ink/45">Vista da AZNext: la aggiorna l&apos;import.</p>}
      </div>
      <div className="flex gap-2">
        {onAnnulla && (
          <Button type="button" variant="ghost" onClick={onAnnulla}>
            Annulla
          </Button>
        )}
        {/* Senza USER non c'è un'utenza: è la stessa regola della rotta. */}
        <Button type="button" loading={isSaving} disabled={!username.trim()} onClick={salva}>
          {!iniziale && <Plus className="h-4 w-4" aria-hidden="true" />}
          {etichettaSalva}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conto tecnico per cassaforte
// ---------------------------------------------------------------------------

function ContiTecnici({ sedeId, dati, onCambiato }: { sedeId: string; dati: UtentiContiSede; onCambiato: () => void }) {
  const { showToast } = useToast();
  const [inModifica, setInModifica] = useState<string | null>(null);
  const [valore, setValore] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  async function salva(deviceClienteId: string) {
    setIsSaving(true);
    try {
      await api.salvaContoTecnico(sedeId, deviceClienteId, valore.trim() || null);
      setInModifica(null);
      showToast("Conto tecnico salvato.", "success");
      onCambiato();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare il conto tecnico."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="mb-6">
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink/45">
        <Vault className="h-3.5 w-3.5" aria-hidden="true" />
        Conto tecnico per cassaforte
      </h3>
      {dati.casseforti.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-4 py-4 text-center text-sm text-ink/45">
          Nessuna cassaforte collocata in questa sede.
        </p>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
          {dati.casseforti.map((c) => (
            <li key={c.device_cliente_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
              <Link to={`/devices/${c.device_id}`} className="text-sm font-semibold text-primary hover:underline">
                {c.matricola}
              </Link>
              {inModifica === c.device_cliente_id ? (
                <div className="flex flex-1 flex-wrap items-end justify-end gap-2">
                  <div className="min-w-[180px]">
                    <Input
                      aria-label={`Conto tecnico della cassaforte ${c.matricola}`}
                      value={valore}
                      onChange={(e) => setValore(e.target.value)}
                      maxLength={50}
                      placeholder="Vuoto per toglierlo"
                    />
                  </div>
                  <Button type="button" variant="ghost" onClick={() => setInModifica(null)}>
                    Annulla
                  </Button>
                  <Button type="button" loading={isSaving} onClick={() => salva(c.device_cliente_id)}>
                    Salva
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className={c.conto_tecnico ? "font-mono text-sm text-ink" : "text-sm text-ink/40"}>
                    {c.conto_tecnico ?? "Nessun conto tecnico"}
                  </span>
                  <BadgeOrigine origine={c.conto_tecnico_origine} />
                  {c.conto_tecnico_aggiornato_il && <span className="text-xs text-ink/40">{dataOra(c.conto_tecnico_aggiornato_il)}</span>}
                  {dati.can_edit_conto && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Modifica il conto tecnico della cassaforte ${c.matricola}`}
                      onClick={() => {
                        setValore(c.conto_tecnico ?? "");
                        setInModifica(c.device_cliente_id);
                      }}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {dati.can_edit && !dati.can_edit_conto && dati.casseforti.length > 0 && (
        <p className="mt-2 text-xs text-ink/45">Per modificare il conto tecnico serve anche il permesso di modifica dei dispositivi.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conto di accredito
// ---------------------------------------------------------------------------

/**
 * Il conto di accredito effettivo, come lo risolve il server
 * (`utils/ibanSede.ts`): proprio della sede, conto interno, o ereditato
 * dall'azienda. La distinzione è l'informazione che conta: correggere l'IBAN
 * dell'azienda tocca solo le sedi che lo ereditano.
 */
function ContoAccredito({ sedeId, dati, onCambiato }: { sedeId: string; dati: UtentiContiSede; onCambiato: () => void }) {
  const a = dati.accredito;
  const [inModifica, setInModifica] = useState(false);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink/45">
          <Landmark className="h-3.5 w-3.5" aria-hidden="true" />
          Conto di accredito
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink/45">
            {a.su_conto_interno ? "Conto interno di questa sede" : a.sovrascritto ? "Conto proprio di questa sede" : "Ereditato dall'azienda"}
          </span>
          {a.sovrascritto && <BadgeOrigine origine={a.origine} />}
          {dati.can_edit && !inModifica && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setInModifica(true)}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Modifica
            </Button>
          )}
        </div>
      </div>

      {inModifica ? (
        <FormAccredito
          sedeId={sedeId}
          accredito={a}
          onAnnulla={() => setInModifica(false)}
          onSalvato={() => {
            setInModifica(false);
            onCambiato();
          }}
        />
      ) : a.su_conto_interno ? (
        // Conto interno: niente IBAN, e niente eredità dall'azienda — sarebbe un
        // conto diverso da quello su cui la sede accredita davvero.
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Campo etichetta="Conto interno" valore={a.conto_interno ?? "Numero non indicato"} forte />
          <Campo etichetta="Beneficiario" valore={a.beneficiario} />
          <Campo etichetta="Banca" valore={a.banca} />
        </div>
      ) : a.iban ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Campo etichetta="IBAN" valore={a.iban} forte />
          <Campo etichetta="Banca" valore={a.banca} />
          {/* Vuota quando si eredita: la filiale non è censita a livello di azienda. */}
          <Campo etichetta="Agenzia" valore={a.agenzia} />
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-line px-4 py-4 text-center text-sm text-ink/45">
          Nessun conto di accredito, né su questa sede né sull&apos;azienda.
        </p>
      )}
    </div>
  );
}

function Campo({ etichetta, valore, forte = false }: { etichetta: string; valore: string | null; forte?: boolean }) {
  return (
    <div>
      <p className="text-xs text-ink/45">{etichetta}</p>
      <p className={forte ? "break-all font-mono text-sm font-semibold text-ink" : "text-sm text-ink"}>{valore ?? "—"}</p>
    </div>
  );
}

type TipoForm = "iban" | "conto_interno" | "nessuno";

function FormAccredito({
  sedeId,
  accredito,
  onAnnulla,
  onSalvato,
}: {
  sedeId: string;
  accredito: UtentiContiSede["accredito"];
  onAnnulla: () => void;
  onSalvato: () => void;
}) {
  const { showToast } = useToast();
  // Banca, agenzia e beneficiario si propongono solo se sono della sede: quelli
  // ereditati descrivono il conto dell'azienda, non un conto da copiare qui.
  const proprio = accredito.sovrascritto;
  const [tipo, setTipo] = useState<TipoForm>(accredito.su_conto_interno ? "conto_interno" : proprio && accredito.iban ? "iban" : "nessuno");
  const [iban, setIban] = useState(proprio && accredito.iban ? accredito.iban : "");
  const [contoInterno, setContoInterno] = useState(accredito.conto_interno ?? "");
  const [beneficiario, setBeneficiario] = useState(proprio ? accredito.beneficiario ?? "" : "");
  const [banca, setBanca] = useState(proprio ? accredito.banca ?? "" : "");
  const [agenzia, setAgenzia] = useState(proprio ? accredito.agenzia ?? "" : "");
  const [isSaving, setIsSaving] = useState(false);

  async function salva() {
    setIsSaving(true);
    try {
      const extra = { beneficiario: beneficiario.trim() || null, banca: banca.trim() || null, agenzia: agenzia.trim() || null };
      await api.salvaAccredito(
        sedeId,
        tipo === "iban"
          ? { tipo: "iban", iban, ...extra }
          : tipo === "conto_interno"
            ? { tipo: "conto_interno", conto_interno: contoInterno, ...extra }
            : { tipo: null }
      );
      showToast("Conto di accredito salvato.", "success");
      onSalvato();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare il conto di accredito."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  const scelte: { valore: TipoForm; etichetta: string }[] = [
    { valore: "iban", etichetta: "IBAN" },
    { valore: "conto_interno", etichetta: "Conto interno" },
    { valore: "nessuno", etichetta: "Nessuno: usa quello dell'azienda" },
  ];

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-line p-3">
      <fieldset className="flex flex-wrap gap-4">
        <legend className="sr-only">Tipo di accredito</legend>
        {scelte.map((s) => (
          <label key={s.valore} className="flex items-center gap-2 text-sm text-ink">
            <input type="radio" name="tipo-accredito" checked={tipo === s.valore} onChange={() => setTipo(s.valore)} />
            {s.etichetta}
          </label>
        ))}
      </fieldset>

      {tipo !== "nessuno" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {tipo === "iban" ? (
            <Input label="IBAN" value={iban} onChange={(e) => setIban(e.target.value)} autoComplete="off" />
          ) : (
            <Input label="Numero del conto interno" value={contoInterno} onChange={(e) => setContoInterno(e.target.value)} maxLength={50} />
          )}
          <Input label="Beneficiario" value={beneficiario} onChange={(e) => setBeneficiario(e.target.value)} />
          <Input label="Banca" value={banca} onChange={(e) => setBanca(e.target.value)} />
          <Input label="Agenzia" value={agenzia} onChange={(e) => setAgenzia(e.target.value)} />
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onAnnulla}>
          Annulla
        </Button>
        <Button
          type="button"
          loading={isSaving}
          disabled={(tipo === "iban" && !iban.trim()) || (tipo === "conto_interno" && !contoInterno.trim())}
          onClick={salva}
        >
          Salva
        </Button>
      </div>
    </div>
  );
}
